import {unstable_batchedUpdates} from 'react-dom'
import type {Environment as RelayEnvironment} from 'react-relay'
import type {
  RequestParameters,
  Variables,
  TaskScheduler,
  CacheConfig,
  INetwork,
  GraphQLTaggedNode,
  OperationType,
  GraphQLResponse,
} from 'relay-runtime'
import {
  Environment,
  Network,
  Observable,
  RecordSource,
  Store,
  createOperationDescriptor,
  fetchQuery,
  generateClientID,
  getRequest,
} from 'relay-runtime'
import type {GraphQLResult, GraphQLSubscriptionResult} from '@github-ui/fetch-graphql'
import fetchGraphQL from '@github-ui/fetch-graphql'
import {missingFieldHandlers} from './missing-field-handlers'
import {subscribe} from './alive-subscription'
import {scheduleLowPriority} from './scheduler'
import {wasServerRendered, IS_BROWSER} from '@github-ui/ssr-utils'
import {inSoftNav} from '@github-ui/soft-nav/utils'

import type {Sink} from 'relay-runtime/lib/network/RelayObservable'
import {
  CACHE_EVENTS,
  createCache,
  QUERY_CACHE_VERSION,
  INTERNAL_ONLY_ATTRIBUTES,
  PERSISTER_KEY_PREFIX,
  retrieveQueryFromHotCache,
  type QueryCache,
} from '@github-ui/relay-query-cache'
import {sendEvent} from '@github-ui/hydro-analytics'
import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {INVALID, dispatchCacheHitEvent, tryReportPendingHPCEvent} from '@github-ui/relay-query-cache/create-query-cache'
import {createVariablesKey} from '@github-ui/relay-query-cache/helpers'

type QueryId = string
type SerializedParams = string
type GraphQLResultWithMetadata = GraphQLResult & {injectedByServiceWorker?: boolean}
type ParamsToResult = Map<SerializedParams, GraphQLResultWithMetadata>
type fetchRelayInternalType = {
  params: RequestParameters
  variables: Variables
  observer: Sink<GraphQLResponse>
  ssrPreloadedQueries?: Map<QueryId, ParamsToResult>
  baseUrl?: string
  emitWarning?: (message: string) => void
  enabledFeatures: FeatureFlagMap
  maxAge?: number
  cache?: QueryCache<GraphQLResponse>
  metadata?: Record<string, unknown>
  stalePreloadedData: boolean
  preloadSubscriptions?: (resolved: GraphQLResponse | undefined) => void
}

export type FeatureFlagMap = {
  [key: string]: boolean
}

type CacheMetadata = {
  cacheTTL: number | undefined
  defaultValues: Record<string, unknown> | undefined
  keysToExclude: string[] | undefined
  preheatSource: string | undefined
  skipCache: boolean | undefined
  skipNetwork: boolean | undefined
  cacheEnabledForQuery: boolean
}

function extractCacheMetadata(metadata: Record<string, unknown> | undefined): CacheMetadata {
  const cacheTTL = metadata?.ttl as number | undefined
  const defaultValues = metadata?.defaultValues as Record<string, unknown> | undefined
  const keysToExclude = metadata?.[INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY] as string[] | undefined
  const preheatSource = metadata?.preheatSource as string | undefined
  const skipCache = metadata?.skipCache as boolean | undefined
  const skipNetwork = metadata?.[INTERNAL_ONLY_ATTRIBUTES.SKIP_NETWORK] as boolean | undefined
  const cacheEnabledForQuery = cacheTTL !== undefined && cacheTTL > 0

  return {cacheTTL, defaultValues, keysToExclude, preheatSource, skipCache, skipNetwork, cacheEnabledForQuery}
}

// Wrapped helper that first tries to fetch from preloaded data in the JSON
// island and falls back to the API if nothing is there.
export function fetchRelayInternal({
  params,
  variables,
  ssrPreloadedQueries,
  baseUrl,
  emitWarning,
  enabledFeatures,
  maxAge,
  observer,
  cache,
  metadata,
  stalePreloadedData,
  preloadSubscriptions,
}: fetchRelayInternalType) {
  if (!params.id) throw new Error('params has no id property!')
  const isClientSideCachingEnabled = isLoggedIn()
  const defaultValues = params.metadata?.defaultValues as Record<string, unknown> | undefined

  // try to get the query from the environment options via the preloaded queries
  if (params.id && ssrPreloadedQueries) {
    const variablesKey = createVariablesKey(variables, defaultValues)
    const result = ssrPreloadedQueries.get(params.id)?.get(variablesKey)

    const currentTimestamp = Math.floor(Date.now() / 1000)
    const responseTimestamp = result?.timestamp

    const isStale = maxAge && responseTimestamp ? currentTimestamp > responseTimestamp + maxAge : false
    if (result) {
      // we have a preloaded query notifying the observer with the result
      observer.next(result)
      const queryParameters = {
        queryId: params.id,
        queryName: params.name,
        variables,
      }
      let fromServiceWorker = false
      if (result.injectedByServiceWorker) {
        dispatchCacheHitEvent('service-worker')
        tryReportPendingHPCEvent(queryParameters, 'hit-sw')
        fromServiceWorker = true
      }

      // if the query is not stale we can complete the observer
      // otherwise we need to fetch the query from the server to make sure it is up to date
      if (!isStale && !stalePreloadedData) {
        if (isClientSideCachingEnabled && cache) {
          const cacheTTL = params.metadata?.ttl as number | undefined
          const cacheEnabledForQuery = cacheTTL !== undefined && cacheTTL > 0
          if (cacheEnabledForQuery) {
            // Prefer server-driven TTL from the preloaded result over the static route config
            const serverCacheTTL = (result as {serverCacheTTL?: number}).serverCacheTTL
            const effectiveTtl = serverCacheTTL ?? cacheTTL
            const keysToExclude = params.metadata?.[INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY] as
              | string[]
              | undefined
            cache.tryCacheFetchResult(
              queryParameters,
              result,
              effectiveTtl,
              {
                query_name: params.name,
                update_type: 'preloaded',
                server_cache_ttl: serverCacheTTL,
                [INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY]: keysToExclude,
              },
              defaultValues,
            )
            if (!fromServiceWorker) {
              tryReportPendingHPCEvent(queryParameters, 'ssr')
            }
          }
        }
        observer.complete()
        return
      }
    }

    if (
      params.metadata?.isRelayRouteRequest &&
      wasServerRendered() &&
      emitWarning &&
      (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') &&
      !inSoftNav()
    ) {
      if (ssrPreloadedQueries.size === 0) {
        emitWarning(
          'Relay SSR Error: There where no preloaded queries found. \n' +
            'More context can be found here: https://thehub.github.com/support/ecosystem/api-graphql/training/graphql-relay/#ssr-error-1',
        )
      } else {
        const queryMap = ssrPreloadedQueries.get(params.id)
        if (!queryMap) {
          emitWarning(
            `Relay SSR Error: There where no preloaded queries found for ${params.id} \n` +
              'More context can be found here: https://thehub.github.com/support/ecosystem/api-graphql/training/graphql-relay/#ssr-error-2',
          )
        } else {
          emitWarning(
            `Relay SSR Error: There where no preloaded queries found for ${params.id} with variables ${variablesKey} \n` +
              `The persisted query (${params.id}) contains preloaded queries for the following set of variables: \n` +
              `${Array.from(queryMap.keys()).join('\n')} \n` +
              'More context can be found here: https://thehub.github.com/support/ecosystem/api-graphql/training/graphql-relay/#ssr-error-3',
          )
        }
      }
    }
  }
  const combinedMetadata = {...(metadata ?? {}), ...params.metadata}

  if (IS_BROWSER && isClientSideCachingEnabled && params.operationKind !== 'mutation') {
    const {
      keysToExclude,
      preheatSource,
      skipCache,
      cacheEnabledForQuery,
      defaultValues: defaultValuesExtracted,
    } = extractCacheMetadata(combinedMetadata)

    if (cacheEnabledForQuery && !preheatSource && !skipCache) {
      const prefix = `${PERSISTER_KEY_PREFIX}-${getCurrentUserLogin()}`
      const hotCacheResult = retrieveQueryFromHotCache<GraphQLResponse>(
        prefix,
        {
          queryId: params.id,
          queryName: params.name,
          variables,
        },
        defaultValuesExtracted,
        keysToExclude,
      )
      if (hotCacheResult && hotCacheResult !== INVALID) {
        preloadSubscriptions?.(hotCacheResult as GraphQLResponse)
        observer.next(hotCacheResult as GraphQLResponse)
        dispatchCacheHitEvent('hot-cache')
      }
    }
  }

  if (IS_BROWSER) {
    return fetchRelayInternalAsync({
      params,
      variables,
      baseUrl,
      enabledFeatures,
      observer,
      isClientSideCachingEnabled,
      cache,
      metadata: combinedMetadata,
      preloadSubscriptions,
    })
  }
}

// Async internal helper that handles cache checking and network fetching
async function fetchRelayInternalAsync({
  params,
  variables,
  baseUrl,
  enabledFeatures,
  observer,
  isClientSideCachingEnabled,
  cache,
  metadata,
  preloadSubscriptions,
}: {
  params: RequestParameters
  variables: Variables
  baseUrl?: string
  enabledFeatures: FeatureFlagMap
  observer: Sink<GraphQLResponse>
  isClientSideCachingEnabled: boolean
  cache?: QueryCache<GraphQLResponse>
  metadata?: Record<string, unknown>
  preloadSubscriptions?: (resolved: GraphQLResponse | undefined) => void
}) {
  if (!params.id) throw new Error('Params has no id property!')

  const {cacheTTL, defaultValues, keysToExclude, preheatSource, skipCache, skipNetwork, cacheEnabledForQuery} =
    extractCacheMetadata(metadata)

  const method = params.operationKind === 'mutation' ? 'POST' : 'GET'
  const queryFn = (isCacheRefresh?: boolean) => {
    return fetchGraphQL(
      params.id,
      params.name,
      variables,
      method,
      baseUrl,
      enabledFeatures,
      observer,
      isCacheRefresh,
      skipCache ? 'force_refresh' : preheatSource,
    )
  }

  if (!cache || !isClientSideCachingEnabled || !cacheEnabledForQuery || params.operationKind === 'mutation') {
    return queryFn()
  }
  return cache.persisterFn(
    {
      queryFn,
      observer,
      preloadSubscriptions,
    },
    {
      queryId: params.id,
      queryName: params.name,
      variables,
    },
    cacheTTL,
    {
      preheat_source: preheatSource,
      [INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY]: keysToExclude,
      [INTERNAL_ONLY_ATTRIBUTES.SKIP_NETWORK]: skipNetwork,
      skip_cache: skipCache,
    },
    defaultValues,
  )
}

// this fixes a bug in relay where children can be re-rendered with deleted relay data
// before the parent re-renders, causing a crash. This is a workaround provided by the relay team
// in https://github.com/facebook/relay/issues/3514
const RelayScheduler: TaskScheduler = {
  cancel: () => false,
  schedule: task => {
    unstable_batchedUpdates(task)
    return ''
  },
}

type ServerPreloadedData<T> = Map<string, T>
export type ServerPreloadedSubscription = ServerPreloadedData<GraphQLSubscriptionResult>
export type ServerPreloadedQuery = ServerPreloadedData<GraphQLResult>

export class SSRNetwork {
  relayNetwork: INetwork
  ssrPreloadedData: Map<string, ServerPreloadedQuery>
  preloadedSubscriptions: Map<string, ServerPreloadedSubscription>
  warningsShown: Set<string>
  enabled_features: FeatureFlagMap
  maxAge?: number
  cache?: QueryCache<GraphQLResponse>
  stalePreloadedData: boolean
  markPreloadedDataAsStale?: (value: boolean) => void
  constructor(
    ssrPreloadedData: Map<string, ServerPreloadedQuery>,
    enabled_features: FeatureFlagMap = {},
    preloadedSubscriptions: Map<string, ServerPreloadedSubscription>,
    baseUrl?: string,
    maxAge?: number,
    cache?: QueryCache<GraphQLResponse>,
    stalePreloadedData?: boolean,
    markPreloadedDataAsStale?: (value: boolean) => void,
  ) {
    this.ssrPreloadedData = ssrPreloadedData
    this.enabled_features = enabled_features
    this.preloadedSubscriptions = preloadedSubscriptions
    this.warningsShown = new Set()
    this.maxAge = maxAge
    this.cache = cache
    this.stalePreloadedData = stalePreloadedData ?? false
    this.markPreloadedDataAsStale = markPreloadedDataAsStale
    this.relayNetwork = Network.create(
      (params: RequestParameters, variables: Variables, cacheConfig: CacheConfig) => {
        return Observable.create(observer => {
          const response = fetchRelayInternal({
            params,
            variables,
            ssrPreloadedQueries: this.ssrPreloadedData,
            baseUrl,
            emitWarning: (message: string) => {
              // In development, throw errors for SSR mismatches to make them catastrophically visible.
              // In CI (GITHUB_ACTIONS), test, and production, log warnings with deduplication for graceful degradation.
              // Note: We use GITHUB_ACTIONS instead of GITHUB_CI because GITHUB_CI is explicitly unset by
              // github/github's script/ci/snek-support.sh before the Rails server starts.
              if (process.env.NODE_ENV === 'development' && !process.env.GITHUB_ACTIONS) {
                throw new Error(message)
              }
              if (!this.warningsShown.has(message)) {
                this.warningsShown.add(message)
                // eslint-disable-next-line no-console
                console.warn(message)
              }
            },
            enabledFeatures: this.enabled_features,
            maxAge,
            observer,
            cache: this.cache,
            metadata: cacheConfig.metadata,
            stalePreloadedData: this.stalePreloadedData,
            preloadSubscriptions: this.preloadSubscriptions.bind(this),
          })

          if (response instanceof Promise) {
            // eslint-disable-next-line github/no-then
            response.then(resolved => {
              if (!Array.isArray(resolved)) {
                this.preloadSubscriptions(resolved)
              }

              if (resolved) {
                observer.next(resolved)
              }
              observer.complete()
            })
          }
          // define that any mutation might change preloaded data so we always refetch after a mutation
          if (params.operationKind === 'mutation') {
            this.stalePreloadedData = true
            this.markPreloadedDataAsStale?.(true)
          }
        })
      },
      (params: RequestParameters, variables: Variables) => {
        return subscribe(params, variables, this.preloadedSubscriptions)
      },
    )
  }

  preloadSubscriptions(resolved: GraphQLResponse | undefined) {
    if (resolved && 'extensions' in resolved && resolved.extensions && resolved.extensions.subscriptions) {
      const subscriptions = resolved.extensions.subscriptions
      for (const queryId in subscriptions) {
        if (!this.preloadedSubscriptions.has(queryId)) {
          this.preloadedSubscriptions.set(queryId, new Map())
        }
        for (const variablesKey in subscriptions[queryId]) {
          this.preloadedSubscriptions.get(queryId)?.set(variablesKey, subscriptions[queryId][variablesKey])
        }
      }
    }
  }

  execute(
    request: RequestParameters,
    variables: Variables,
    cacheConfig: CacheConfig,
    // uploadables: UploadableMap | undefined = undefined,
  ) {
    return this.relayNetwork.execute(request, variables, cacheConfig)
  }

  getPreloadedSubscriptions() {
    return this.preloadedSubscriptions
  }
}

// Relay environment with missingFieldHandlers. This is used mainly for controlled rollout of missingFieldHandlers.
// Once we gain confidence in these changes we will upstream to the main Environment instance.
export const relayEnvironmentWithMissingFieldHandlerForNode = (baseUrl?: string): Environment => {
  let queryCache = undefined
  if (IS_BROWSER) {
    queryCache = createCache<GraphQLResponse>({
      sendAnalyticsEvent: (eventType, target?, payload = {}) => {
        try {
          const context = {
            react: true,
            version: QUERY_CACHE_VERSION,
          }
          sendEvent(eventType, {...context, ...payload, target})
        } catch {
          // Analytics must not throw — the octolytics meta tag may be
          // absent (e.g. during test teardown or on pages without analytics).
        }
      },
      reportableEvents: CACHE_EVENTS.ANY,
    })
  }

  const customNetwork = new SSRNetwork(new Map(), {}, new Map(), baseUrl, undefined, queryCache)
  const localEnvironment = new Environment({
    scheduler: RelayScheduler,
    store: new Store(new RecordSource(), {
      // Keep a buffer of at most 50 released queries to avoid them being disposed. Helpful for back forward
      // navigation in short succession alleviates the need for a query to go out to the network if a component
      // is remounted quickly.
      gcReleaseBufferSize: 50,
      // Keep stale entries around for a maximum of 5 mins.
      queryCacheExpirationTime: 5 * 60 * 1e3,
      // Don't garbage collect immediately, wait at least 10s before performing garbage collection.
      // See: https://relay.dev/docs/guided-tour/reusing-cached-data/presence-of-data/#gc-scheduler
      gcScheduler: scheduleLowPriority,
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDataID: (fieldValue: {[key: string]: any}, typeName: string) => {
      // This is basically the default (defaultGetDataID.js) implementation
      // Special handling for ProjectV2SingleSelectFieldOption due to it having a non-unique id field
      if (typeName === 'Viewer') {
        return fieldValue.id == null ? generateClientID('client:root', 'viewer') : fieldValue.id
      }
      if (typeName === 'ProjectV2SingleSelectFieldOption') {
        const idFields: Array<string | number> = []
        if (fieldValue.id) idFields.push(fieldValue.id)
        if (fieldValue.name) idFields.push(fieldValue.name)
        if (fieldValue.color) idFields.push(fieldValue.color)
        if (fieldValue.description) idFields.push(getHash(fieldValue.description))
        return idFields.join('_')
      }
      return fieldValue.id
    },
    network: customNetwork,
    missingFieldHandlers,
  })

  localEnvironment.options = {
    baseUrl,
    getPreloadedSubscriptions: () => {
      return customNetwork.getPreloadedSubscriptions()
    },
  }
  return localEnvironment
}

function getHash(input: string) {
  let hash = 0
  if (input.length === 0) return hash
  for (let i = 0; i < input.length; i++) {
    const chr = input.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0
  }
  return hash
}

export function clientSideRelayFetchQueryRetained<T extends OperationType>({
  environment,
  query,
  variables = {},
  metadata,
}: {
  environment: RelayEnvironment
  query: GraphQLTaggedNode
  variables?: Record<string, unknown>
  metadata?: Record<string, unknown>
}) {
  const queryRequest = getRequest(query)
  const queryDescriptor = createOperationDescriptor(queryRequest, variables)
  environment.retain(queryDescriptor)
  return fetchQuery<T>(environment, query, variables, {
    fetchPolicy: 'store-or-network',
    networkCacheConfig: {
      metadata,
    },
  })
}

/**
 * Re-export of RelayEnvironmentProvider from react-relay.
 * This is the standard import location for non-test, non-story code across the monorepo.
 * For test and story files, use @github-ui/relay-test-utils instead.
 *
 * @example
 * ```tsx
 * import {RelayEnvironmentProvider} from '@github-ui/relay-environment'
 * import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
 *
 * const relayEnvironment = relayEnvironmentWithMissingFieldHandlerForNode()
 *
 * function App() {
 *   return (
 *     <RelayEnvironmentProvider environment={relayEnvironment}>
 *       <YourComponent />
 *     </RelayEnvironmentProvider>
 *   )
 * }
 * ```
 */
// eslint-disable-next-line no-barrel-files/no-barrel-files -- Intentional re-export to centralize relay provider imports
export {RelayEnvironmentProvider} from 'react-relay'
